// Pilot usage analytics — append-only product telemetry for the app owner.
//
// Privacy contract (why this can ship without a FERPA conversation):
//   - Events NEVER carry student identifiers. The prop sanitizer below drops
//     any key that smells like student data before it leaves the client, and
//     firestore.rules independently rejects docs containing those keys.
//   - Reads are owner-only (isUsageAnalyticsOwner in rules). School admins
//     cannot see how their own staff use the app — this is pilot telemetry
//     for the product owner, not an employee-monitoring surface.
//   - Writes are fire-and-forget: an analytics failure must never break or
//     slow the feature being measured.

import { doc, setDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db, auth } from '@/firebase'
import { USAGE_EVENTS } from '@/config/analytics'

// Prop keys matching any of these substrings (case-insensitive) are stripped.
// Mirrors blockedKeys in the usageEvents create rule in firestore.rules.
const BLOCKED_PROP_KEY_PARTS = ['student', 'ssid', 'name', 'email']

const MAX_PROP_VALUE_LENGTH = 120
const MAX_PROPS = 12

function sanitizeProps(props) {
  if (!props || typeof props !== 'object') return {}
  const clean = {}
  for (const [key, value] of Object.entries(props)) {
    if (Object.keys(clean).length >= MAX_PROPS) break
    const lower = key.toLowerCase()
    if (BLOCKED_PROP_KEY_PARTS.some((bad) => lower.includes(bad))) continue
    if (value == null) continue
    if (typeof value === 'string') {
      clean[key] = value.slice(0, MAX_PROP_VALUE_LENGTH)
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      clean[key] = value
    }
    // Objects/arrays are dropped — keep events flat and small.
  }
  return clean
}

// Same sessionStorage key as auditLogger so both systems agree on what a
// "session" is (one browser tab until close / session-timeout sign-out).
function getSessionId() {
  if (!sessionStorage.getItem('sessionId')) {
    sessionStorage.setItem(
      'sessionId',
      `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    )
  }
  return sessionStorage.getItem('sessionId')
}

async function getActiveSchoolId() {
  try {
    const { useSchoolStore } = await import('@/store/schoolStore')
    return useSchoolStore().currentSchoolId || null
  } catch {
    return null
  }
}

let enabled = true

/**
 * Write one usage event. Never throws, never awaited by callers that care
 * about UX latency — call it and move on.
 * @param {string} event one of USAGE_EVENTS
 * @param {object} fields { feature?, route?, props? }
 */
async function track(event, { feature = null, route = null, props = {} } = {}) {
  if (!enabled) return
  const user = auth.currentUser
  if (!user) return

  try {
    const schoolId = await getActiveSchoolId()
    await setDoc(doc(collection(db, 'usageEvents')), {
      event,
      ...(feature ? { feature } : {}),
      ...(route ? { route } : {}),
      props: sanitizeProps(props),
      userId: user.uid,
      userEmail: user.email || 'unknown',
      // Role comes from the users doc, not the JWT, so it stays accurate even
      // pre-claim-sync; read it lazily to avoid an authStore import cycle.
      userRole: await getUserRole(),
      ...(schoolId ? { schoolId } : {}),
      sessionId: getSessionId(),
      host: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
      timestamp: serverTimestamp()
    })
  } catch (error) {
    // Deliberately silent-ish: one console line, no user-facing impact.
    console.warn('usage analytics event dropped:', error?.message || error)
  }
}

async function getUserRole() {
  try {
    const { useAuthStore } = await import('@/store/authStore')
    return useAuthStore().currentUser?.role || 'unknown'
  } catch {
    return 'unknown'
  }
}

/** Route change → page view. `routeName` is the vue-router name (no params,
 *  so no student/aide/feedback ids leak into analytics). */
function trackPageView(routeName) {
  if (!routeName || routeName === 'NotFound' || routeName === 'Login') return
  track(USAGE_EVENTS.PAGE_VIEW, { route: String(routeName) })
}

/** Product feature usage, e.g. trackFeature('schedule_import', { updated: 42 }). */
function trackFeature(feature, props = {}) {
  track(USAGE_EVENTS.FEATURE, { feature, props })
}

/** One per authenticated user per tab session — the DAU/WAU signal. Fired by
 *  the auth store when a profile with a role is observed; deduped by uid so a
 *  logout→login as a different user in the same tab still counts. */
function trackSessionStart(uid) {
  const key = `usageSessionTracked:${uid}`
  if (sessionStorage.getItem(key)) return
  sessionStorage.setItem(key, '1')
  track(USAGE_EVENTS.SESSION_START)
}

export const usageAnalytics = {
  track,
  trackPageView,
  trackFeature,
  trackSessionStart,
  setEnabled(value) {
    enabled = value
  }
}

export default usageAnalytics
